import React, { createContext, useContext, ReactNode, useMemo, useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { TenantRoleType } from '../types/User';
import PortalComponentsService from '../services/PortalComponentsService';
import { adminUrlTenant, resolveAdminPortalTenantId } from '../utils/portalAdminTenantOverride';
import { getRuntimeConfig } from '../runtime/runtimeConfig';
import { setActiveTenant } from '../services/activeTenant';
import type { PortalComponentId } from '../types/PortalComponents';

/** `{component id: visible}` for the calling user on the portal's tenant. */
export type PortalVisibleComponents = Partial<Record<PortalComponentId, boolean>>;

interface PortalTenantContextType {
  tenantId: string;
  tenantRole: TenantRoleType;
  /** Admin scoped via ?tenant= (or persisted override) for cross-tenant handoffs. */
  isAdminTenantOverride: boolean;
  /**
   * Which portal components (tab groups) the CALLING user sees on this tenant,
   * from `GET /api/portal/{tenantId}/components` — the portal's only source of
   * tab visibility. The backend resolves the whole rule
   * (licensed ∧ enabled ∧ available ∧ role), so a bound tenant, an admin's
   * ?tenant= override and a normal user's own tenant are the same fetch and the
   * preview shows exactly what a real user of that tenant sees.
   */
  visibleComponents: PortalVisibleComponents;
}

const PortalTenantContext = createContext<PortalTenantContextType | undefined>(undefined);

interface PortalTenantProviderProps {
  children: ReactNode;
}

export const PortalTenantProvider: React.FC<PortalTenantProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { t } = useTranslation('portal');
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const firstTenantRole = user?.tenant_roles?.[0];

  // Tenant mode: this origin is one tenant's own container. The tenant is
  // fixed by the server, for everyone including admins; the ?tenant= override
  // and the persisted admin override do not apply.
  const runtime = getRuntimeConfig();
  const boundTenant = runtime.mode === 'tenant' ? runtime.tenant_id : null;

  // Admins run the cross-tenant assistant, so their own portal tenant (if any)
  // rarely matches the chat's. A ?tenant=<id> override lets an admin deep-link
  // into a specific tenant's portal — e.g. the supplier-credential handoff — and
  // on the HITL review the case tenant is taken from the handoff token (see
  // adminUrlTenant), so the whole shell matches the chat/resolve rather than the
  // admin's default tenant. The override is persisted for the tab session so
  // in-portal navigation (which drops the params) keeps the same tenant. Only
  // honored for admins; a portal/end user is always scoped to their own role.
  const urlTenant = user?.role === 'admin' && !boundTenant ? adminUrlTenant(searchParams, location.pathname) : null;
  const tenantOverride = useMemo(
    () => (user?.role === 'admin' && !boundTenant ? resolveAdminPortalTenantId(urlTenant) : null),
    [user?.role, urlTenant, boundTenant],
  );

  // Publish the portal's tenant to the API layer (X-Tenant-Id).
  const effectiveTenant = boundTenant ?? tenantOverride ?? firstTenantRole?.tenant_id ?? null;
  useEffect(() => {
    setActiveTenant(effectiveTenant);
    return () => setActiveTenant(null);
  }, [effectiveTenant]);

  // One fetch per tenant, for every user. Exposed via context so the predicate
  // downstream (header / routes / doc resolver) is synchronous and consistent.
  const [visible, setVisible] = useState<PortalVisibleComponents | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!effectiveTenant) return;
    const controller = new AbortController();
    setVisible(null);
    setLoadError(false);
    PortalComponentsService.get(effectiveTenant, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        const map: PortalVisibleComponents = {};
        for (const c of res.components) map[c.id] = c.visible;
        setVisible(map);
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadError(true);
      });
    return () => controller.abort();
  }, [effectiveTenant, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  if (!effectiveTenant) {
    return null;
  }

  // Fail closed: showing tabs from a guess would be the very bug the single
  // source fixes. A distinct error (not the generic "no agents" empty state)
  // tells the user the portal setup could not be read, with a retry.
  if (loadError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2, px: 2, textAlign: 'center' }}>
        <Typography variant="h6">{t('errors.configLoadTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('errors.configLoadBody', { tenantId: effectiveTenant })}
        </Typography>
        <Button variant="outlined" onClick={retry}>{t('errors.retry')}</Button>
      </Box>
    );
  }
  if (visible === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (boundTenant) {
    const roleHere = user?.tenant_roles?.find((tr) => tr.tenant_id === boundTenant);
    return (
      <PortalTenantContext.Provider value={{
        tenantId: boundTenant,
        // Admins may hold no role on this tenant; nothing gates on tenantRole
        // today, so 'owner' keeps nothing hidden for them.
        tenantRole: roleHere?.role ?? (user?.role === 'admin' ? 'owner' : 'viewer'),
        isAdminTenantOverride: false,
        visibleComponents: visible,
      }}>
        {children}
      </PortalTenantContext.Provider>
    );
  }

  if (tenantOverride) {
    return (
      <PortalTenantContext.Provider value={{
        tenantId: tenantOverride,
        // No portal page gates on tenantRole today; default to the admin's own
        // role on that tenant if present, else 'owner' so nothing is hidden.
        tenantRole: firstTenantRole?.role ?? 'owner',
        isAdminTenantOverride: true,
        visibleComponents: visible,
      }}>
        {children}
      </PortalTenantContext.Provider>
    );
  }

  return (
    <PortalTenantContext.Provider value={{
      tenantId: effectiveTenant,
      tenantRole: firstTenantRole?.role ?? 'viewer',
      isAdminTenantOverride: false,
      visibleComponents: visible,
    }}>
      {children}
    </PortalTenantContext.Provider>
  );
};

export const usePortalTenant = (): PortalTenantContextType => {
  const context = useContext(PortalTenantContext);
  if (context === undefined) {
    throw new Error('usePortalTenant must be used within a PortalTenantProvider');
  }
  return context;
};

export default PortalTenantContext;
